// Single entry point for opening a link that leaves the app. On native this is
// the in-app browser (SFSafariViewController / Chrome Custom Tabs) so the user
// never gets app-switched; everywhere else it stays a new tab, exactly as
// before.

import { Capacitor } from '@capacitor/core'
import { captureHandledError } from '@/plugins/sentry/scope'
import { expandHexColor } from '@/utils/hex-color'
import {
  TOOLBAR_COLOR_TOKEN,
  WEB_LINK_TARGET,
  WEB_LINK_FEATURES,
} from '@/constants/inAppBrowser'

/**
 * Hand a URL to the system browser, escaping the app entirely.
 *
 * Use this only when leaving the app is the point — e.g. an App Store / Play
 * link, which the OS turns into a store-app hand-off. The in-app browser would
 * just render the store's web page instead.
 */
export function openInSystemBrowser(url) {
  if (!url) return
  window.open(url, WEB_LINK_TARGET, WEB_LINK_FEATURES)
}

// Read the theme at call time — it is a reactive ref and the user can toggle
// light/dark while the app is running.
//
// Vuetify is imported lazily and only on the native path: importing it eagerly
// pulls its stylesheets into every consumer's module graph, which breaks the
// node-environment unit tests of anything that imports this file.
async function resolveToolbarColor() {
  const { default: vuetify } = await import('@/plugins/vuetify')
  return expandHexColor(vuetify.theme.global.current.value.colors?.[TOOLBAR_COLOR_TOKEN])
}

/**
 * Open an external URL the way this platform should.
 *
 * `presentationStyle` is deliberately not passed: it is iOS-only and already
 * defaults to fullscreen.
 */
export async function openExternalUrl(url) {
  if (!url) return

  if (!Capacitor.isNativePlatform()) {
    openInSystemBrowser(url)
    return
  }

  try {
    const { Browser } = await import('@capacitor/browser')
    const toolbarColor = await resolveToolbarColor()
    await Browser.open({ url, ...(toolbarColor ? { toolbarColor } : {}) })
  } catch (error) {
    // A plugin failure must never swallow the user's tap.
    captureHandledError(error)
    openInSystemBrowser(url)
  }
}
